import React, { ReactElement } from 'react';

import ToolbarTool from '../../../ToolbarTool';
import Icon from '../../../../../../components/icons/Icon';
import SheetContainer, {
    CloseButton,
    Content,
    Header,
    Heading,
    Overlay,
    Sheet,
} from '../../../../../../mobile/structural/sheet';
import type { UseSheetReturnType } from '../../../../../../mobile/structural/sheet/hooks/useSheet';
import LabelsSearchBar from '../components/LabelsSearchBar';
import LabelLists from '../components/LabelLists';
import type { LabelsToolController } from '../hooks/labelsToolTypes';
import type { Label } from '../../../../../../../common/labels/labelTypes';

type MobileGenericLabelsToolProps = {
    gridSize: number;
    name: string;
    sheetProps: UseSheetReturnType;
    controller: LabelsToolController;
    handleClickEdit?: (label: Label) => void;
};

/** Shared mobile label tool shell for element-owned and task-item-owned controllers. */
const MobileGenericLabelsTool = ({
    gridSize,
    name,
    sheetProps,
    controller,
    handleClickEdit,
}: MobileGenericLabelsToolProps): ReactElement => {
    const { isSheetOpen, dispatchOpenSheet } = sheetProps;
    const {
        allLabels,
        selectedLabelIds,
        hasCompletedInitialFetch,
        searchString,
        setSearchString,
        handleCreateLabel,
        handleSearchSubmit,
        handleToggleLabelSelection,
    } = controller;

    return (
        <>
            <ToolbarTool name={name} isOpen={isSheetOpen} onClick={() => dispatchOpenSheet()}>
                <Icon gridSize={gridSize} name="toolbar-label" />
            </ToolbarTool>
            <SheetContainer
                className="LabelsToolbarSheet"
                {...sheetProps}
                defaultSnapPoint={0.5}
                snapPoints={[0.5, 0.9]}
            >
                <Overlay />
                <Sheet>
                    <Header>
                        <Heading>{name}</Heading>
                        <CloseButton />
                    </Header>
                    <LabelsSearchBar
                        value={searchString}
                        onChange={setSearchString}
                        handleSearchSubmit={handleSearchSubmit}
                        autoFocus={false}
                        className="!px-4"
                        size="lg"
                    />
                    <Content className="px-3.5 py-1.5">
                        <LabelLists
                            handleToggleLabelSelection={handleToggleLabelSelection}
                            handleCreateLabel={handleCreateLabel}
                            selectedLabelIds={selectedLabelIds}
                            searchString={searchString}
                            allLabels={allLabels}
                            hasCompletedInitialFetch={hasCompletedInitialFetch}
                            handleClickEdit={handleClickEdit}
                            isSheet
                            size="lg"
                        />
                    </Content>
                </Sheet>
            </SheetContainer>
        </>
    );
};

export default MobileGenericLabelsTool;
